/**
 * useIsMobile.js - Responsive breakpoint detection hook
 * 
 * Provides reactive screen size detection for mobile-responsive layouts.
 * 
 * Breakpoints:
 * - Mobile: 0 - 767px
 * - Tablet: 768px - 1023px
 * - Desktop: 1024px+
 * 
 * Usage:
 *   const isMobile = useIsMobile();
 *   const isTablet = useIsMobile(1024) && !useIsMobile(768);
 */

import { useState, useEffect } from 'react';

// Breakpoint constants
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1440
};

/**
 * Hook to detect if viewport is below a breakpoint
 * @param {number} breakpoint - Width threshold in pixels (default: 768)
 * @returns {boolean} - True if viewport width is less than breakpoint
 */
export function useIsMobile(breakpoint = BREAKPOINTS.mobile) {
  const [isMobile, setIsMobile] = useState(() => {
    // Initial state based on current window width
    if (typeof window !== 'undefined') {
      return window.innerWidth < breakpoint;
    }
    return false;
  });

  useEffect(() => {
    // Handler for resize events
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Check on mount in case initial state was wrong
    handleResize();

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

/**
 * Hook to get current breakpoint name
 * @returns {string} - 'mobile' | 'tablet' | 'desktop'
 */
export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < BREAKPOINTS.mobile) return 'mobile';
      if (width < BREAKPOINTS.tablet) return 'tablet';
      return 'desktop';
    }
    return 'desktop';
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < BREAKPOINTS.mobile) {
        setBreakpoint('mobile');
      } else if (width < BREAKPOINTS.tablet) {
        setBreakpoint('tablet');
      } else {
        setBreakpoint('desktop');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
}

export default useIsMobile;
